import { ArrowDownTrayIcon, ChevronDownIcon, ChevronUpIcon, ClipboardDocumentIcon, ScaleIcon, XMarkIcon } from "@heroicons/react/20/solid";
import type { AuditScoreType, Transport } from "@ots-frontend/ots-data";
import type { ReactNode } from "react";
import { AdminPanel } from "../components/AdminPanel";
import { DescriptionItem, DescriptionList } from "../components/DescriptionList";
import { OtsBadge, type OtsBadgeTone } from "../components/OtsBadge";
import { OtsTransportCategoryIcon } from "../components/OtsTransportCategoryIcon";
import { OtsActionButton, OtsIconActionButton } from "../components/OtsButton";
import { OtsFileTypeIcon } from "../components/OtsFileTypeIcon";
import { type OtsGalleryImage, OtsImageGallery } from "../components/OtsImageGallery";
import { OtsConfidenceRing } from "../components/OtsConfidenceRing";
import { OtsInlineAlert } from "../components/OtsInlineAlert";
import { OtsMessageList } from "../components/OtsMessageList";
import { OtsTimeline, type OtsTimelineItem } from "../components/OtsTimeline";
import {
  formatActivityDateTime,
  formatActivityDuration,
  formatActivityMoney,
  formatActivityQuantity,
  formatActivityTime,
} from "./driver-activity-format";
import { auditScoreLabels, auditSummaryFacts, formatConfidence, toConfidenceRatio } from "../audit/AuditScoreRing";
import { activityKindTheme } from "./driver-activity-theme";
import { ActivityGeofenceChip } from "./ActivityGeofenceChip";
import { type ActivityEntry, type ActivityGeofenceMatch, type ActivityMarkerTone, isActivityTicket, secondScadaQty } from "./driver-activity-view-model";

export type DriverActivityInspectorProps = {
  entry: ActivityEntry;
  timeZone?: string;
  showRevenue?: boolean;
  /** Which audit confidence leads the Audit section. */
  auditScoreType?: AuditScoreType;
  onClose?: () => void;
  /** Step to the row above or below without going back to the list; omit at either end. */
  onPrevious?: () => void;
  onNext?: () => void;
  /** Opens the full audit (scores, findings, data checks, Run Audit) in its dialog; adds View more audit details. */
  onOpenAudit?: () => void;
  /** A map of the ticket's route, shown under Route. */
  map?: ReactNode;
  /** Opens the attachment at this index in the ticket's carousel. */
  onOpenAttachment?: (index: number) => void;
  /** Downloads all of the ticket's attachments. */
  onDownloadAttachments?: () => void;
  /** The open entry's actions, e.g. icon buttons for View, Compliance, downloads, comments, and attachments. */
  actions?: ReactNode;
  /** Runs one of the host's actions on this entry, e.g. a gauge quantity audit from the Quantity section. */
  onRunAction?: (actionId: string) => void;
  /** Whether `onRunAction` can run this action here; controls offered only when it can. */
  canRunAction?: (actionId: string) => boolean;
  className?: string;
};

const markerBadgeTones: Record<ActivityMarkerTone, OtsBadgeTone> = {
  neutral: "neutral", info: "info", success: "success", warning: "warning", danger: "danger", violet: "violet",
};

/** Puts text on the clipboard where the browser allows it; a no-op elsewhere. */
const copyText = (text: string) => void navigator.clipboard?.writeText(text).catch(() => undefined);

const Section = ({ title, children }: { title: string; children: ReactNode }) =>
  <AdminPanel variant="section" title={title}>{children}</AdminPanel>;

const Facts = ({ facts }: { facts: [label: string, value: ReactNode][] }) => (
  <DescriptionList layout="inline">
    {facts.filter(([, value]) => value != null && value !== "" && value !== false).map(([label, value]) => (
      <DescriptionItem key={label} label={label}>{value}</DescriptionItem>
    ))}
  </DescriptionList>
);

const stopItem = (side: "Pickup" | "Delivery", transport: Transport | undefined, time: number | undefined, timeZone?: string, geofence?: ActivityGeofenceMatch): OtsTimelineItem => transport
  ? {
    id: side,
    tone: side === "Pickup" ? "success" : "danger",
    label: <span className="font-semibold">{side} · {transport.siteName ?? "Unknown site"}</span>,
    description: <>
      {[transport.containerName, transport.siteCompanyName].filter(Boolean).join(" · ")}
      <br/>
      {formatActivityDateTime(time, timeZone)} · {formatActivityQuantity(transport.quantityDecimal, transport.quantityUnitOfMeasure)}
      {transport.onSiteDuration != null && ` · ${formatActivityDuration(transport.onSiteDuration)} on site`}
      <span className="mt-1 flex items-center gap-1.5">
        <ActivityGeofenceChip match={geofence} ticketTime={time} timeZone={timeZone}/>
        {geofence
          ? `Geofence ${formatActivityTime(geofence.event.inDttm, timeZone)} – ${geofence.event.outDttm != null ? formatActivityTime(geofence.event.outDttm, timeZone) : "still on site"}`
          : "No geofence matched"}
      </span>
    </>,
  }
  : { id: side, tone: "warning", label: <span className="font-semibold">No {side.toLowerCase()} matched</span> };

/** Everything about one row of the activity list, in place of the legacy viewer's dialogs and hover text. */
export const DriverActivityInspector = ({ entry, timeZone, showRevenue = false, auditScoreType = "BUSINESS_VALIDATION_CONFIDENCE", onClose, onPrevious, onNext, onOpenAttachment, onDownloadAttachments, onOpenAudit, map, actions, onRunAction, canRunAction, className }: DriverActivityInspectorProps) => {
  const { pickup, delivery, tta, geofenceEvent, dispatch, serviceWork, containerVolumeDrop: drop } = entry;
  const transport = pickup ?? delivery;
  const theme = activityKindTheme[entry.kind];
  const kind = { label: theme.label, tone: theme.badgeTone };
  const markers = [...entry.markers, ...entry.pickupMarkers, ...entry.deliveryMarkers]
    .filter((marker, index, all) => all.findIndex((other) => other.id === marker.id) === index);
  const oil = pickup ?? delivery;
  const hasOilQuality = oil?.observedGravity != null || oil?.bswPercentage != null || oil?.observedTemperature != null || oil?.netQuantity != null;
  const ticketNumbers = [pickup?.ticketNumber, delivery?.ticketNumber].filter(Boolean).join(" → ");
  const { audit } = entry;
  const title = isActivityTicket(entry)
    ? entry.resourceId ?? (ticketNumbers || "Ticket")
    : geofenceEvent?.siteName ?? serviceWork?.siteName ?? drop?.containerName ?? dispatch?.ticketNumber ?? kind.label;
  const flags = Object.values(entry.flags)
    .filter((flag) => flag.tone !== "success" && !entry.warnings.some((warning) => warning.message === flag.message))
    // One rule can flag two cells, e.g. both sites of an operator mismatch.
    .filter((flag, index, all) => all.findIndex((other) => other.rule === flag.rule) === index);
  const costs = entry.rateModels;

  return (
    <aside aria-label={`${kind.label} details`} // `!flex`: a host reset (Mantine's) may set `aside` and `header` to block outside any CSS layer.
      className={["!flex min-h-0 flex-col bg-[var(--ots-surface)] text-[var(--ots-text)]", className].filter(Boolean).join(" ")}>
      <header className="!flex items-start gap-2 px-4 py-3">
        <div className="min-w-0 flex-1">
          <span className="flex items-center gap-1.5">
            <OtsBadge tone={kind.tone} size="compact">{kind.label}</OtsBadge>
            {entry.isServiceTicket && <OtsBadge tone="yellow" size="compact">Service work</OtsBadge>}
            {entry.category && <OtsBadge size="compact" icon={<OtsTransportCategoryIcon category={entry.category}/>}>{entry.category.toLowerCase()}</OtsBadge>}
          </span>
          <h2 className="mt-1 flex items-center gap-1 font-[family-name:var(--ots-font-display)] text-base font-semibold">
            <span className="truncate">{title}</span>
            {entry.resourceId && (
              <OtsIconActionButton variant="text" size="mini" icon={<ClipboardDocumentIcon/>} label="Copy ID" onClick={() => copyText(entry.resourceId as string)}/>
            )}
          </h2>
          {entry.resourceId && ticketNumbers && <p className="truncate text-sm font-medium tabular-nums">{ticketNumbers}</p>}
          <p className="truncate text-xs text-[var(--ots-text-muted)]">
            {[transport?.personName ?? geofenceEvent?.personName ?? dispatch?.personName ?? serviceWork?.personName, formatActivityDateTime(entry.time, timeZone)].filter(Boolean).join(" · ")}
          </p>
        </div>
        {(onPrevious || onNext) && <span className="flex">
          <OtsIconActionButton variant="text" size="compact" icon={<ChevronUpIcon/>} label="Previous row" disabled={!onPrevious} onClick={onPrevious}/>
          <OtsIconActionButton variant="text" size="compact" icon={<ChevronDownIcon/>} label="Next row" disabled={!onNext} onClick={onNext}/>
        </span>}
        {onClose && <OtsIconActionButton variant="text" size="compact" icon={<XMarkIcon/>} label="Close details" onClick={onClose}/>}
      </header>
      {markers.length > 0 && (
        <ul aria-label="Ticket facts" className="flex flex-wrap gap-1.5 px-4 pb-3">
          {markers.map((marker) => <li key={marker.id}><OtsBadge tone={markerBadgeTones[marker.tone]} size="compact">{marker.label}</OtsBadge></li>)}
        </ul>
      )}
      {actions != null && <div role="toolbar" aria-label="Ticket actions" className="flex flex-wrap items-center gap-1 px-4 pb-3">{actions}</div>}

      <div className="min-h-0 flex-1 overflow-y-auto">
        {(entry.warnings.length > 0 || flags.length > 0) && (
          <Section title="Needs review">
            <div className="space-y-2">
              {entry.warnings.map((warning) => <OtsInlineAlert key={warning.type} tone="danger" size="compact">{warning.message}</OtsInlineAlert>)}
              {flags.map((flag) => <OtsInlineAlert key={flag.rule} tone={flag.tone === "danger" ? "danger" : "warning"} size="compact">{flag.message}</OtsInlineAlert>)}
            </div>
          </Section>
        )}

        {(audit || onOpenAudit) && (
          <Section title="Audit">
            {/* A summary only: the findings, data checks, Refresh, and Run Audit are in the audit dialog. */}
            {audit?.hasAuditRun ? <>
              <div className="mb-2 flex items-center gap-2.5">
                <OtsConfidenceRing
                  size={36}
                  value={toConfidenceRatio(audit.confidences[auditScoreType])}
                  blocked={audit.blockingFindingCount > 0}
                  label={`${auditScoreLabels[auditScoreType]} confidence ${formatConfidence(audit.confidences[auditScoreType])}`}/>
                <div>
                  <p className="text-lg font-semibold leading-tight tabular-nums">{formatConfidence(audit.confidences[auditScoreType])}</p>
                  <p className="text-xs text-[var(--ots-text-muted)]">{auditScoreLabels[auditScoreType]} confidence</p>
                </div>
                {audit.blockingFindingCount > 0 && <OtsBadge tone="danger" size="compact" className="ml-auto">{audit.blockingFindingCount} blocking</OtsBadge>}
              </div>
              <Facts facts={auditSummaryFacts(audit, timeZone)}/>
            </> : <p className="text-sm text-[var(--ots-text-muted)]">This ticket has not been audited yet.</p>}
            {onOpenAudit && (
              <OtsActionButton size="compact" variant="text" className="mt-2 !px-1" onClick={onOpenAudit}>View more audit details</OtsActionButton>
            )}
          </Section>
        )}

        {entry.attachments.length > 0 && (
          <Section title={`Attachments (${entry.attachments.length})`}>
            <OtsImageGallery
              ariaLabel="Ticket attachments"
              thumbnailSize={72}
              images={entry.attachments.map((attachment): OtsGalleryImage => ({
                id: attachment.id,
                src: attachment.url,
                name: attachment.name,
                caption: attachment.name,
                icon: attachment.kind === "image" ? undefined : <OtsFileTypeIcon kind={attachment.kind}/>,
              }))}
              onOpen={onOpenAttachment && ((_, index) => onOpenAttachment(index))}/>
            {onDownloadAttachments && (
              <OtsActionButton size="compact" variant="text" icon={<ArrowDownTrayIcon/>} className="mt-2 !px-1" onClick={onDownloadAttachments}>
                {entry.attachments.length === 1 ? "Download" : `Download all (${entry.attachments.length})`}
              </OtsActionButton>
            )}
          </Section>
        )}

        {isActivityTicket(entry) && <>
          <Section title="Route">
            <OtsTimeline
              ariaLabel="Pickup and delivery"
              density="compact"
              items={[
                stopItem("Pickup", pickup, tta?.pickupDttm ?? pickup?.createDttm, timeZone, entry.pickupGeofence),
                stopItem("Delivery", delivery, tta?.deliveryDttm ?? delivery?.createDttm, timeZone, entry.deliveryGeofence),
              ]}/>
            {map != null && <div className="mt-3">{map}</div>}
          </Section>
          <Section title="Quantity">
            <Facts facts={[
              ["Pickup ticket", pickup && formatActivityQuantity(pickup.quantityDecimal, pickup.quantityUnitOfMeasure)],
              ["Delivery ticket", delivery && formatActivityQuantity(delivery.quantityDecimal, delivery.quantityUnitOfMeasure)],
              ["Driver entered", pickup?.driverQty != null && formatActivityQuantity(pickup.driverQty, pickup.quantityUnitOfMeasure)],
              ["SCADA", pickup?.scadaQty != null && formatActivityQuantity(pickup.scadaQty, pickup.quantityUnitOfMeasure)],
              ["SCADA 2", pickup && secondScadaQty(pickup) != null && formatActivityQuantity(secondScadaQty(pickup), pickup.quantityUnitOfMeasure)],
              ...(["pickup", "delivery"] as const).map((side): [string, ReactNode] => {
                const stop = side === "pickup" ? pickup : delivery;
                const actionId = `gauge-audit-${side}`;
                const audited = !!onRunAction && (canRunAction?.(actionId) ?? true);
                // As WaterGo's ticket details: the calculated gauge, with how it was derived (or why it was not) a click away.
                return [`${side === "pickup" ? "Pickup" : "Delivery"} gauge`, stop && (stop.gaugeQty != null || audited) && (
                  <span className="inline-flex items-center gap-1">
                    {stop.gaugeQty != null ? formatActivityQuantity(stop.gaugeQty, stop.quantityUnitOfMeasure) : <span className="font-normal text-[var(--ots-text-muted)]">Not set</span>}
                    {audited && <OtsIconActionButton variant="text" size="mini" icon={<ScaleIcon/>} label={`How was the ${side} gauge quantity calculated?`} onClick={() => onRunAction?.(actionId)}/>}
                  </span>
                )];
              }),
              ["Quantity locked", tta?.qtyValidatedDttm != null && formatActivityDateTime(tta.qtyValidatedDttm, timeZone)],
            ]}/>
          </Section>
          <Section title="Details">
            <Facts facts={[
              ["Freight", pickup?.waterTypeName ?? delivery?.waterTypeName],
              ["Work type", pickup?.serviceTypeName ?? delivery?.serviceTypeName],
              ["Pickup ticket #", pickup?.ticketNumber],
              ["Delivery ticket #", delivery?.ticketNumber],
              ["Vehicle", transport?.vehicleName],
              ["Trailer", transport?.trailerName],
              ["Tank battery", [...new Set([pickup?.containerClusterName, delivery?.containerClusterName].filter(Boolean))].join(" → ")],
              ["Well", transport?.wellName],
              ["Routes", transport?.routeNames?.join(", ")],
              ["Work order", transport?.workOrderNumber],
              ["Dispatcher", tta?.dispatcherName],
              ["Requested by", tta?.requesterName],
            ]}/>
          </Section>
          <Section title="Time and distance">
            <Facts facts={[
              ["Turnaround", entry.durations.turnaround != null && formatActivityDuration(entry.durations.turnaround)],
              ["On site", entry.durations.onSite != null && formatActivityDuration(entry.durations.onSite)],
              ["Travel", entry.durations.travel != null && formatActivityDuration(entry.durations.travel)],
              ["Billable", entry.durations.billable != null && formatActivityDuration(entry.durations.billable)],
              ["Loaded miles", tta?.loadedMileage != null && formatActivityQuantity(tta.loadedMileage)],
              ["Unloaded miles", tta?.unloadedMileage != null && formatActivityQuantity(tta.unloadedMileage)],
              ["Total miles", tta?.mileage != null && formatActivityQuantity(tta.mileage)],
              ["Projected miles", tta?.projectedMileage != null && formatActivityQuantity(tta.projectedMileage)],
            ]}/>
          </Section>
          {hasOilQuality && (
            <Section title="Oil quality">
              <Facts facts={[
                ["Observed gravity", oil?.observedGravity],
                ["Observed temperature", oil?.observedTemperature != null && `${oil.observedTemperature}°${oil.temperatureUom ?? ""}`],
                ["BS&W", oil?.bswPercentage != null && `${oil.bswPercentage}%`],
                ["Corrected gravity", oil?.gravityAt60],
                ["Net quantity", oil?.netQuantity != null && formatActivityQuantity(oil.netQuantity, oil.quantityUnitOfMeasure)],
              ]}/>
            </Section>
          )}
          {!!tta?.ticketCodeList?.length && (
            <Section title="Coding">
              <Facts facts={tta.ticketCodeList.map((code): [string, ReactNode] => [
                code.afe ?? code.code ?? code.wellName ?? "Account",
                `${[code.wellName, code.stage].filter(Boolean).join(" · ")} ${code.amountPercent}%`.trim(),
              ])}/>
            </Section>
          )}
        </>}

        {geofenceEvent && (
          <Section title="Visit">
            <Facts facts={[
              ["Site", geofenceEvent.siteName],
              ["Owner", geofenceEvent.siteCompanyName],
              ["Site group", geofenceEvent.geofenceSiteGroupName],
              ["Arrived", formatActivityDateTime(geofenceEvent.inDttm, timeZone)],
              ["Left", geofenceEvent.outDttm != null ? formatActivityDateTime(geofenceEvent.outDttm, timeZone) : "Still on site"],
              ["On site", entry.durationMillis != null && formatActivityDuration(entry.durationMillis)],
              ["Vehicle", geofenceEvent.vehicleName],
            ]}/>
          </Section>
        )}

        {serviceWork && (
          <Section title="Site work">
            <Facts facts={[
              ["Work type", serviceWork.serviceTypeName],
              ["Site", serviceWork.siteName],
              ["Owner", serviceWork.siteCompanyName],
              ["Started", formatActivityDateTime(serviceWork.startDttm, timeZone)],
              ["Finished", serviceWork.endDttm != null ? formatActivityDateTime(serviceWork.endDttm, timeZone) : "In progress"],
              ["Duration", entry.durationMillis != null && formatActivityDuration(entry.durationMillis)],
              ["Billable hours", serviceWork.billableHours],
              ["Charge", showRevenue && serviceWork.totalCost != null && formatActivityMoney(serviceWork.totalCost)],
            ]}/>
          </Section>
        )}

        {drop && (
          <Section title="Tank level drop">
            <Facts facts={[
              ["Site", drop.siteName],
              ["Tank", drop.containerName],
              ["Owner", drop.siteCompanyName],
              ["From", formatActivityDateTime(drop.startDttm, timeZone)],
              ["To", formatActivityDateTime(drop.endDttm, timeZone)],
              ["Volume", formatActivityQuantity(drop.volume, drop.unitOfMeasure)],
              ["Fill rate", `${formatActivityQuantity(drop.fillRate, drop.unitOfMeasure)}/h`],
              ["Cargo", drop.cargoSubtype.toLowerCase()],
            ]}/>
          </Section>
        )}

        {dispatch && (
          <Section title="Dispatch">
            <Facts facts={[
              ["Status", dispatch.status?.toLowerCase()],
              ["Scheduled", formatActivityDateTime(dispatch.scheduleDttm, timeZone)],
              ["Pickup", dispatch.pickupSiteName],
              ["Delivery", dispatch.deliverySiteName],
              ["Freight", dispatch.waterTypeName],
              ["Quantity", dispatch.quantityDecimal != null && formatActivityQuantity(dispatch.quantityDecimal, dispatch.quantityUnitOfMeasure)],
              ["Comments", dispatch.comments],
            ]}/>
          </Section>
        )}

        {entry.validation.length > 0 && (
          <Section title="Validation">
            <OtsTimeline
              ariaLabel="Validation steps"
              density="compact"
              items={entry.validation.map((step) => ({
                id: step.id,
                tone: step.validatedDttm != null ? "success" : "neutral",
                label: step.label,
                description: step.validatedDttm != null
                  ? `${step.validatorName ?? "Validated"} · ${formatActivityTime(step.validatedDttm, timeZone)}`
                  : "Not validated",
              }))}/>
          </Section>
        )}

        {showRevenue && costs.length > 0 && (
          <Section title="Revenue and cost">
            <Facts facts={[
              ...costs.map((model): [string, ReactNode] => [model.rateModelName ?? "Rate model", formatActivityMoney(model.totalCost ?? 0)]),
              ...costs.filter((model) => (model.demurrageCost ?? 0) > 0).map((model): [string, ReactNode] => [`${model.rateModelName ?? "Rate model"} demurrage`, formatActivityMoney(model.demurrageCost)]),
              ["Revenue", <strong key="total">{formatActivityMoney(entry.revenue)}</strong>],
              ["Driver and trailer cost", !!entry.cost && formatActivityMoney(entry.cost)],
            ]}/>
          </Section>
        )}

        {entry.messages.length > 0 && (
          <Section title="Comments">
            <OtsMessageList
              ariaLabel="Comments"
              density="compact"
              items={entry.messages.map((message) => ({
                id: message.id,
                body: message.message,
                authorName: message.personName,
                author: message.personName,
                timestamp: formatActivityDateTime(message.createDttm, timeZone),
              }))}/>
          </Section>
        )}
      </div>
    </aside>
  );
};
